import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import predictiveAnalyticsService from '../../services/predictiveAnalyticsService';
import errorMonitoringService from '../../services/errorMonitoringService';
import infrastructureMonitoringService from '../../services/infrastructureMonitoringService';
import PredictiveDashboardCard from './components/PredictiveDashboardCard';
import PredictiveModelingPanel from './components/PredictiveModelingPanel';
import RootCauseAnalysisPanel from './components/RootCauseAnalysisPanel';
import AutomatedResponsePanel from './components/AutomatedResponsePanel';
import PredictionTimelinePanel from './components/PredictionTimelinePanel';
import toast, { Toaster } from 'react-hot-toast';

const AIPoweredSystemIntelligencePredictiveAnalyticsCenter = () => {
  const [activeTab, setActiveTab] = useState('predictive_modeling');
  const [predictions, setPredictions] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [rootCauseAnalysis, setRootCauseAnalysis] = useState(null);
  const [interventionHistory, setInterventionHistory] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [errors, apiMetrics, dbMetrics] = await Promise.all([
        errorMonitoringService?.getAllErrors({ limit: 50 }),
        infrastructureMonitoringService?.getApiPerformanceSummary(),
        infrastructureMonitoringService?.getDatabasePerformanceSummary()
      ]);

      setSystemHealth({
        errorCount: errors?.length || 0,
        apiHealth: apiMetrics,
        databaseHealth: dbMetrics
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast?.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRunPredictiveAnalysis = async () => {
    try {
      setAnalyzing(true);
      toast?.loading('Running AI-powered predictive analysis...');

      const historicalData = {
        recentErrors: await errorMonitoringService?.getAllErrors({ limit: 100 }),
        apiMetrics: await infrastructureMonitoringService?.getApiPerformanceMetrics({ limit: 50 }),
        databaseMetrics: await infrastructureMonitoringService?.getDatabasePerformanceMetrics({ limit: 50 })
      };

      const analysisResult = await predictiveAnalyticsService?.analyzePredictivePatterns(historicalData);
      
      setPredictions(analysisResult?.predictions || []);
      setSystemHealth(prev => ({
        ...prev,
        healthScore: analysisResult?.systemHealthScore,
        riskLevel: analysisResult?.riskLevel
      }));

      toast?.dismiss();
      toast?.success(`Analysis complete: ${analysisResult?.predictions?.length || 0} predictions generated`);
    } catch (error) {
      console.error('Predictive analysis error:', error);
      toast?.dismiss();
      toast?.error('Failed to run predictive analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRootCauseAnalysis = async (error) => {
    try {
      setSelectedError(error);
      toast?.loading('Performing AI-powered root cause analysis...');

      const analysis = await predictiveAnalyticsService?.performRootCauseAnalysis(error);
      setRootCauseAnalysis(analysis);

      toast?.dismiss();
      toast?.success('Root cause analysis complete');
      setActiveTab('root_cause_analysis');
    } catch (err) {
      console.error('Root cause analysis error:', err);
      toast?.dismiss();
      toast?.error('Failed to perform root cause analysis');
    }
  };

  const handleExecuteIntervention = async (interventionType, parameters) => {
    try {
      toast?.loading(`Executing ${interventionType?.replace('_', ' ')}...`);

      const result = await predictiveAnalyticsService?.executeAutomatedIntervention(interventionType, parameters);
      
      setInterventionHistory(prev => [{
        type: interventionType,
        result,
        timestamp: new Date()?.toISOString()
      }, ...prev]);

      toast?.dismiss();
      toast?.success(result?.message || 'Intervention executed successfully');
    } catch (error) {
      console.error('Intervention execution error:', error);
      toast?.dismiss();
      toast?.error('Failed to execute intervention');
    }
  };

  const handleDetectAnomalies = async () => {
    try {
      const currentMetrics = {
        cpuUsage: 75,
        avgResponseTime: 450,
        errorRate: 5.2
      };

      const baseline = {
        avgCpuUsage: 45,
        avgResponseTime: 150,
        avgErrorRate: 1.2
      };

      const detectedAnomalies = await predictiveAnalyticsService?.detectAnomalies(currentMetrics, baseline);
      setAnomalies(detectedAnomalies);

      if (detectedAnomalies?.length > 0) {
        toast?.error(`${detectedAnomalies?.length} anomalies detected`);
      } else {
        toast?.success('No anomalies detected');
      }
    } catch (error) {
      console.error('Anomaly detection error:', error);
      toast?.error('Failed to detect anomalies');
    }
  };

  const tabs = [
    { id: 'predictive_modeling', label: 'Predictive Modeling', icon: 'TrendingUp' },
    { id: 'root_cause_analysis', label: 'Root Cause Analysis', icon: 'Search' },
    { id: 'automated_response', label: 'Automated Response', icon: 'Zap' },
    { id: 'prediction_timeline', label: 'Prediction Timeline', icon: 'Clock' }
  ];

  return (
    <>
      <Helmet>
        <title>AI-Powered System Intelligence & Predictive Analytics Center</title>
      </Helmet>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Icon name="Brain" className="w-8 h-8 text-purple-600" />
                  AI-Powered System Intelligence
                </h1>
                <p className="text-gray-600 mt-1">Predictive failure detection with Anthropic AI-powered root cause analysis</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDetectAnomalies}
                  variant="outline"
                  disabled={loading}
                >
                  <Icon name="AlertTriangle" className="w-4 h-4 mr-2" />
                  Detect Anomalies
                </Button>
                <Button
                  onClick={handleRunPredictiveAnalysis}
                  disabled={analyzing}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Icon name="Sparkles" className="w-4 h-4 mr-2" />
                  {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
                </Button>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            {/* Predictive Analytics Dashboard - 4 cols */}
            <div className="col-span-12 lg:col-span-4">
              <PredictiveDashboardCard
                systemHealth={systemHealth}
                predictions={predictions}
                anomalies={anomalies}
                loading={loading}
              />
            </div>

            {/* Main Intelligence Panel - 8 cols */}
            <div className="col-span-12 lg:col-span-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex overflow-x-auto">
                    {tabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`px-6 py-4 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                          activeTab === tab?.id
                            ? 'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                        }`}
                      >
                        <Icon name={tab?.icon} className="w-4 h-4" />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'predictive_modeling' && (
                    <PredictiveModelingPanel
                      predictions={predictions}
                      onRunAnalysis={handleRunPredictiveAnalysis}
                      analyzing={analyzing}
                    />
                  )}

                  {activeTab === 'root_cause_analysis' && (
                    <RootCauseAnalysisPanel
                      selectedError={selectedError}
                      rootCauseAnalysis={rootCauseAnalysis}
                      onAnalyzeError={handleRootCauseAnalysis}
                    />
                  )}

                  {activeTab === 'automated_response' && (
                    <AutomatedResponsePanel
                      interventionHistory={interventionHistory}
                      onExecuteIntervention={handleExecuteIntervention}
                    />
                  )}

                  {activeTab === 'prediction_timeline' && (
                    <PredictionTimelinePanel
                      predictions={predictions}
                      anomalies={anomalies}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIPoweredSystemIntelligencePredictiveAnalyticsCenter;
